import React, { useState } from "react";
import { Alert, Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
  IpdSectionCard,
} from "../../ipd/utils";
import { addHomeMedication, removeHomeMedication } from "../api/emergencyApi";

/**
 * The medicines a patient was taking before they arrived (ticket 15).
 *
 * A record only — deliberately nothing here or anywhere checks it against
 * prescriptions or orders. The card says so, so nobody reads a clean list as a
 * cleared interaction check.
 */
export default function HomeMedicationsCard({ visit, onChanged, disabled }) {
  const meds = visit.home_medications || [];

  const [medicineName, setMedicineName] = useState("");
  const [dose, setDose] = useState("");
  const [frequency, setFrequency] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setError(e?.message || "That did not work");
    } finally {
      setBusy(false);
    }
  };

  const submit = () =>
    run(async () => {
      await addHomeMedication(visit.id, {
        medicineName: medicineName.trim(),
        dose: dose.trim(),
        frequency: frequency.trim(),
      });
      setMedicineName("");
      setDose("");
      setFrequency("");
    });

  const canAdd = Boolean(medicineName.trim()) && !busy;

  return (
    <IpdSectionCard
      title="Home medications"
      subtitle="What the patient was already taking before arriving — a record only"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {meds.length === 0 ? (
        <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
          No home medications recorded. Add what the patient says they take.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          {meds.map((m) => (
            <Box
              key={m.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                px: 1.75,
                py: 1.1,
                border: `1px solid ${IPD_COLORS.border}`,
                borderRadius: `${IPD_SHAPE.radiusMd}px`,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                  {m.medicine_name}
                  {(m.dose || m.frequency) && (
                    <Typography component="span" sx={{ ml: 1, fontSize: 12.5, color: IPD_COLORS.textSecondary, fontWeight: 400 }}>
                      {[m.dose, m.frequency].filter(Boolean).join(" · ")}
                    </Typography>
                  )}
                </Typography>
                {m.note && (
                  <Typography sx={{ mt: 0.2, fontSize: 12, color: IPD_COLORS.textMuted }}>
                    {m.note}
                  </Typography>
                )}
              </Box>
              {!disabled && (
                <Tooltip title="Remove this medication">
                  <IconButton
                    size="small"
                    disabled={busy}
                    aria-label={`Remove ${m.medicine_name}`}
                    onClick={() => run(() => removeHomeMedication(visit.id, m.id))}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      )}

      {!disabled && (
        <Box
          sx={{
            p: 1.75,
            bgcolor: IPD_COLORS.pageBg,
            border: `1px solid ${IPD_COLORS.border}`,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
          }}
        >
          <Typography sx={{ ...ipdLabelSx, mb: 1.25 }}>Add a home medication</Typography>
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <TextField
              value={medicineName}
              onChange={(e) => setMedicineName(e.target.value)}
              label="Medicine"
              placeholder="Metformin, ramipril…"
              size="small"
              required
              fullWidth
              sx={ipdElevatedInputSx}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
              <TextField
                value={dose}
                onChange={(e) => setDose(e.target.value)}
                label="Dose"
                placeholder="500 mg"
                size="small"
                sx={ipdElevatedInputSx}
              />
              <TextField
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                label="Frequency"
                placeholder="Twice a day"
                size="small"
                sx={ipdElevatedInputSx}
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <IpdButton startIcon={<AddIcon />} onClick={submit} disabled={!canAdd} loading={busy}>
                Add medication
              </IpdButton>
            </Box>
          </Box>
        </Box>
      )}
    </IpdSectionCard>
  );
}
